import * as React from 'react'
import { cn } from '../../lib/utils'
import { ColorDot } from './color-dot'
import {
  type CalendarEvent,
  type CalendarEventColor,
  addDays,
  formatFullDate,
  formatTime,
  isSameDay,
  startOfDay,
} from './event-calendar-utils'

/**
 * EventAgenda — a compact list of upcoming events, in two layouts:
 *
 *  - `grouped` (default): events grouped under day headers ("Today" / "Tomorrow"
 *    / "Wed, Jul 15"), each a slim row (color dot · time · title). Best for a
 *    sidebar or a homepage "Upcoming" card. Also powers `EventCalendarWidget`.
 *  - `detailed`: a schedule list — each row has a per-row date + time column, a
 *    title + description, and optional per-row hover actions (`rowActions`). For
 *    a schedule/reminders panel.
 *
 *   <EventAgenda events={events} onEventClick={openEvent} />
 *   <EventAgenda variant="detailed" events={events} rowActions={renderActions} />
 *
 * Both show events on or after `from` (default today per `now`), sorted by start
 * and capped at `maxEvents`. Ongoing multi-day events are clamped to `from` so an
 * in-progress conference still surfaces at the top.
 */

export type EventAgendaVariant = 'grouped' | 'detailed'

/**
 * A leading color dot for an event, or nothing when the event has no `color`
 * (reminders and other uncolored events render with the title flush — no dot,
 * no reserved gap).
 */
const eventDot = (color: CalendarEventColor | undefined, size: 'sm' | 'md') =>
  color ? <ColorDot color={color} size={size} /> : null

/** "Today" / "Tomorrow" / "Wed, Jul 15" for a day header. */
export const relativeDayLabel = (day: Date, now: Date): string => {
  if (isSameDay(day, now)) return 'Today'
  if (isSameDay(day, addDays(now, 1))) return 'Tomorrow'
  return formatFullDate(day)
}

/** A single grouped-variant row: color dot · time (or "All day") · title · trailing · actions. */
export const AgendaRow = ({
  event,
  onClick,
  trailing,
  actions,
  size = 'md',
}: {
  event: CalendarEvent
  onClick?: () => void
  /** Right-aligned trailing metadata — a recurrence badge, a status pill. */
  trailing?: React.ReactNode
  /**
   * Per-row actions (view / edit / remove) revealed on row hover or focus. When
   * set, the row renders as a static `group` (not a whole-row button) — the
   * actions carry the interactions.
   */
  actions?: React.ReactNode
  /** Row density — `md` (default) or `sm` (denser dot / time / title). */
  size?: EventAgendaSize
}) => {
  const dense = size === 'sm'
  const content = (
    <>
      {eventDot(event.color, dense ? 'sm' : 'md')}
      <span className="w-16 shrink-0 whitespace-nowrap text-xs tabular-nums text-fbui-gray-500">
        {event.allDay ? 'All day' : formatTime(event.start)}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-fbui-gray-800">
        {event.title}
      </span>
      {trailing != null && <span className="shrink-0 pl-2">{trailing}</span>}
    </>
  )
  const base = cn(
    // Square (no rounding) so a `divide-y` list draws straight dividers that
    // don't bow at the row's corners.
    'flex w-full items-center gap-2 px-2 text-left',
    dense ? 'py-1' : 'py-1.5'
  )
  if (actions != null) {
    // The trailing badge sits flush right; on hover the actions slide in over it
    // (a solid cover in the row's hover fill, so they cleanly hide the badge).
    return (
      <div className={cn(base, 'group/agendarow relative transition-colors hover:bg-fbui-gray-50')}>
        {content}
        {/* Arbitrary `[opacity:*]` utilities (not `opacity-0`/`opacity-100`) so
            the class names can't collide with a consumer's global
            `.opacity-0 { opacity: 0 !important }` (Bootstrap ships this in the
            host app; its !important + cascade-layer order pins the overlay
            hidden and even an !important reveal can't win). Unique class names
            side-step the collision entirely. */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-0.5 bg-fbui-gray-50 pl-6 pr-2 [opacity:0] transition-opacity focus-within:pointer-events-auto focus-within:[opacity:1] group-hover/agendarow:pointer-events-auto group-hover/agendarow:[opacity:1]">
          {actions}
        </div>
      </div>
    )
  }
  if (!onClick) {
    return <div className={base}>{content}</div>
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        base,
        'outline-none transition-colors hover:bg-fbui-gray-50 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand'
      )}
    >
      {content}
    </button>
  )
}

export type EventAgendaSize = 'sm' | 'md'

/** Per-size row metrics for the detailed variant. */
const detailedSize: Record<
  EventAgendaSize,
  {
    pad: string
    gap: string
    dateWidth: string
    time: string
    title: string
    dot: 'sm' | 'md'
  }
> = {
  md: {
    pad: 'p-[0.75rem]',
    gap: 'sm:gap-6',
    dateWidth: 'min-w-[6rem]',
    time: 'text-sm',
    title: 'text-sm',
    dot: 'md',
  },
  sm: {
    pad: 'p-2',
    gap: 'sm:gap-4',
    dateWidth: 'min-w-[5rem]',
    time: 'text-xs',
    title: 'text-xs',
    dot: 'sm',
  },
}

/**
 * A detailed-variant row: a date/time column beside a title + description, with
 * optional per-row hover actions. Wrapper:
 *  - `onOpen` (and no actions) → a `<button>` that opens the event;
 *  - otherwise → a static `group` row; `actions` fade in on hover / focus.
 */
const DetailedRow = ({
  event,
  onOpen,
  actions,
  rounded,
  size,
}: {
  event: CalendarEvent
  onOpen?: () => void
  actions?: React.ReactNode
  rounded: boolean
  size: EventAgendaSize
}) => {
  const s = detailedSize[size]
  const hasActions = Boolean(actions)

  const dateBlock = (
    <div className={s.dateWidth}>
      <span className="block text-xs text-fbui-gray-500">
        {formatFullDate(event.start)}
      </span>
      <span className={cn('font-medium text-fbui-gray-800', s.time)}>
        {event.allDay ? 'All day' : formatTime(event.start)}
      </span>
    </div>
  )
  const contentBlock = (
    <div className="min-w-0 flex-1">
      <span
        className={cn(
          'flex items-center gap-2 font-medium text-fbui-gray-800',
          s.title
        )}
      >
        {eventDot(event.color, s.dot)}
        <span className="truncate">{event.title}</span>
      </span>
      {event.description && (
        <span className="mt-0.5 block truncate text-xs text-fbui-gray-500">
          {event.description}
        </span>
      )}
    </div>
  )

  const layout = cn(
    'flex flex-col gap-[0.75rem] sm:flex-row sm:items-center',
    s.pad,
    s.gap,
    rounded && 'rounded-lg'
  )

  // Whole-row click to open, when there are no per-row action buttons.
  if (onOpen && !hasActions) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          layout,
          'w-full text-left outline-none transition-colors hover:bg-fbui-gray-50 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand'
        )}
      >
        {dateBlock}
        {contentBlock}
      </button>
    )
  }
  return (
    <div
      className={cn(
        layout,
        'group/agendarow relative transition-colors hover:bg-fbui-gray-50'
      )}
    >
      {dateBlock}
      {contentBlock}
      {hasActions && (
        // Row actions overlay the right edge and reveal on hover / focus. A
        // solid cover (same gray-50 as the hovered row, so it's seamless) fills
        // the full row height and cleanly hides the title end behind the icons.
        // Arbitrary `[opacity:*]` utilities: see the grouped-variant note —
        // unique class names side-step a consumer's global
        // `.opacity-0 { opacity: 0 !important }` (Bootstrap utilities).
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-stretch [opacity:0] transition-opacity focus-within:pointer-events-auto focus-within:[opacity:1] group-hover/agendarow:pointer-events-auto group-hover/agendarow:[opacity:1]">
          <div
            className={cn(
              'flex items-center gap-0.5 bg-fbui-gray-50 pl-6 pr-[0.75rem]',
              rounded && 'rounded-r-lg'
            )}
          >
            {actions}
          </div>
        </div>
      )}
    </div>
  )
}

export interface EventAgendaProps {
  events: CalendarEvent[]
  /** Layout: `grouped` (day headers) or `detailed` (schedule rows). */
  variant?: EventAgendaVariant
  /** Only show events on or after this date. Defaults to today (`now`). */
  from?: Date
  /** Max events to list (default 8). */
  maxEvents?: number
  /** "Now", for the Today/Tomorrow labels. Defaults to `new Date()`. */
  now?: Date
  /** Called when a row is clicked (omit for static rows). */
  onEventClick?: (event: CalendarEvent) => void
  /**
   * Per-row actions (e.g. view / edit / remove) revealed on row hover or focus,
   * in both the `grouped` and `detailed` variants. Return icon buttons for the
   * given event. (In `grouped`, a row with actions is no longer a whole-row
   * button — the actions carry the interactions.)
   */
  rowActions?: (event: CalendarEvent) => React.ReactNode
  /**
   * Grouped variant: right-aligned trailing metadata per row (a recurrence
   * badge, a status pill). Return a node for the given event, or nothing.
   */
  renderTrailing?: (event: CalendarEvent) => React.ReactNode
  /**
   * Draw divider lines. In the `detailed` variant, separates each row; in the
   * `grouped` variant, separates each date SECTION (not individual rows) — the
   * date-grouped best practice, so events within a day flow on whitespace.
   */
  dividers?: boolean
  /** Detailed variant row size (default `md`). */
  size?: EventAgendaSize
  /** Cap the height and scroll the list (uses the canonical `fbui-scroll`). */
  scroll?: boolean
  /** Text shown when there are no upcoming events. */
  emptyLabel?: string
  className?: string
}

interface DayGroup {
  key: number
  label: string
  events: CalendarEvent[]
}

export const EventAgenda = ({
  events,
  variant = 'grouped',
  from,
  maxEvents = 8,
  now,
  onEventClick,
  rowActions,
  renderTrailing,
  dividers = false,
  size = 'md',
  scroll = false,
  emptyLabel = 'No upcoming events',
  className,
}: EventAgendaProps) => {
  const resolvedNow = now ?? new Date()
  const start = from ?? startOfDay(resolvedNow)
  const scrollClass = scroll ? 'max-h-80 overflow-y-auto fbui-scroll' : ''

  const upcoming = events
    .filter((e) => (e.end ?? e.start) >= start)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .slice(0, maxEvents)

  if (upcoming.length === 0) {
    return (
      <p
        className={cn(
          'px-2 py-6 text-center text-sm text-fbui-gray-500',
          className
        )}
      >
        {emptyLabel}
      </p>
    )
  }

  if (variant === 'detailed') {
    return (
      <div
        className={cn(
          dividers
            ? 'flex flex-col divide-y divide-fbui-gray-100'
            : 'flex flex-col gap-1',
          scrollClass,
          className
        )}
      >
        {upcoming.map((event) => (
          <DetailedRow
            key={event.id}
            event={event}
            rounded={!dividers}
            size={size}
            onOpen={onEventClick && (() => onEventClick(event))}
            actions={rowActions ? rowActions(event) : undefined}
          />
        ))}
      </div>
    )
  }

  // Grouped: bucket by day, clamping already-started events up to `start` so an
  // ongoing multi-day event lands in the first group rather than a past one.
  const groups: DayGroup[] = []
  const byKey = new Map<number, DayGroup>()
  for (const event of upcoming) {
    const eventDay = startOfDay(event.start)
    const day = eventDay < start ? start : eventDay
    const key = day.getTime()
    let group = byKey.get(key)
    if (!group) {
      group = { key, label: relativeDayLabel(day, resolvedNow), events: [] }
      byKey.set(key, group)
      groups.push(group)
    }
    group.events.push(event)
  }

  return (
    <div
      className={cn(
        dividers
          ? 'flex flex-col divide-y divide-fbui-gray-100'
          : 'flex flex-col gap-2',
        scrollClass,
        className
      )}
    >
      {groups.map((group) => (
        // Date-grouped agenda: separate by DATE SECTION, not per row. A subtle
        // rule sits between date groups (not the first); events within a day
        // flow on whitespace under the date label — per-row hairlines read as a
        // dense table fighting the grouping.
        <div
          key={group.key}
          className={cn(
            dividers &&
              'border-t border-fbui-gray-100 py-2 first:border-t-0 first:pt-0 last:pb-0'
          )}
        >
          <p className="px-2 pb-0.5 text-xs font-medium uppercase tracking-wide text-fbui-gray-400">
            {group.label}
          </p>
          <div className="flex flex-col">
            {group.events.map((event) => (
              <AgendaRow
                key={event.id}
                event={event}
                size={size}
                onClick={onEventClick && (() => onEventClick(event))}
                trailing={renderTrailing?.(event)}
                actions={rowActions?.(event)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
EventAgenda.displayName = 'EventAgenda'
